import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  useTheme,
  IconButton,
  Button,
  CircularProgress,
  Stack,
  Avatar,
  Divider,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SubmitIcon from "@mui/icons-material/Send";
import DescriptionIcon from "@mui/icons-material/Description";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useSelector } from "react-redux";
import Papa from "papaparse";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { generateEodFileName } from "../../utils/helpers";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";

const ReportDataDialog = ({
  open,
  onClose,
  title,
  recordForEdit,
  reportType,
  columns,
  onDataLoad,
  onDataEdit,
}) => {
  const theme = useTheme();

  const [showLoader, setShowLoader] = useState(true);
  const [data, setData] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [textContent, setTextContent] = useState("");

  const fontFamily = `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`;

  const clearData = () => {
    setData([]);
    setRows([]);
    setError("");
    setTextContent("");
    setShowLoader(true);
  };

  useEffect(() => {
    if (open) {
      clearData();
      if (onDataLoad) {
        onDataLoad(
          recordForEdit,
          setData,
          setTextContent,
          setShowLoader,
          setError
        );
      }
    }
  }, [open, recordForEdit, onDataLoad]);

  useEffect(() => {
    if (data && data.length > 0) {
      setRows(processDataForGrid());
    }
  }, [data]);

  const processDataForGrid = () => {
    return data
      ?.map((v, i) => {
        if (i === 0 || i === data.length - 1) {
          return null;
        }

        const tempv = {};
        v.forEach((n, j) => {
          tempv[j] = n;
        });
        tempv.id = i;
        return tempv;
      })
      .filter((row) => row !== null);
  };

  const handleDownload = () => {
    try {
      const date = recordForEdit?.date;
      const fileName = generateEodFileName(reportType, date);

      let fileContent;
      let mimeType;
      
      if (reportType?.toLowerCase() === "goldman") {
        // For Goldman reports, use the original text content (pipe-separated)
        fileContent = textContent;
        mimeType = "text/plain;charset=utf-8;";
      } else {
        // For other reports, convert to CSV format
        fileContent = Papa.unparse(data);
        mimeType = "text/csv;charset=utf-8;";
      }

      const blob = new Blob([fileContent], { type: mimeType });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      toast.success("Report downloaded successfully!");
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Error downloading report. Please try again.");
    }
  };

  const handleSubmit = () => {
    const { reportId } = recordForEdit;
    const payload = {
      action: "submit_report",
      data: {
        report: reportId,
      },
    };

    sendWebSocketMessage(JSON.stringify(payload));
    toast.success("Report Submission Initiated.");
    onClose();
  };

  const handleClose = () => {
    clearData();
    onClose();
  };

  const uppercaseValueFormatter = (params) => {
    const v = params.value != null && params.value !== "" ? params.value : "";
    return String(v).toUpperCase();
  };

  const columnsWithUppercase = columns?.map((column) => {
    const base = {
      ...column,
      valueFormatter: column.valueFormatter ?? uppercaseValueFormatter,
    };
    // Explicit renderCell so the value is always visible (avoids display bug where content only shows in edit mode)
    if (!column.renderCell) {
      base.renderCell = (params) => {
        const formatted = (base.valueFormatter ?? uppercaseValueFormatter)(params);
        return (
          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              color: theme.palette.text.primary,
              fontFamily: "inherit",
            }}
          >
            {formatted}
          </Typography>
        );
      };
    }
    return base;
  });

  const handleCellEdit = (params) => {
    if (onDataEdit) {
      onDataEdit(params, rows, setRows);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(textContent);
    toast.success("Text copied to clipboard!");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 4px 24px 0 rgba(16,30,54,0.10)",
          fontFamily,
          minHeight: "80vh",
          maxHeight: "90vh",
          width: "95vw",
          maxWidth: "1400px",
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 4, pt: 3, pb: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 40,
                height: 40,
              }}
            >
              <DescriptionIcon sx={{ color: "#fff", fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 20, sm: 24 },
                  fontFamily,
                  lineHeight: 1.2,
                  color: theme.palette.text.primary,
                }}
              >
                {title}'s Report Data 
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontFamily,
                  mt: 0.5,
                  fontSize: { xs: 12, sm: 14 },
                }}
              >
                View and manage report generation data
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={handleClose}
            size="large"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Divider sx={{ mt: 2, mx: 4 }} />

      {/* Content */}
      <DialogContent 
        dividers={false}
        sx={{ 
          pt: 3, 
          pb: 2, 
          px: { xs: 2, sm: 4 }, 
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {showLoader ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="100%"
                height={{ xs: 300, sm: 400 }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : (
              <>
                {textContent ? (
                  reportType?.toLowerCase() === "goldman" ? (
                    // Goldman report - show text format with copy icon
                    <Box sx={{ height: "100%", width: "100%", position: "relative", display: "flex", flexDirection: "column", minHeight: 0 }}>
                      <Box display="flex" justifyContent="flex-end" sx={{ mb: 1, position: "absolute", top: 8, right: 8, zIndex: 1 }}>
                        <IconButton onClick={handleCopy} size="small">
                          <FileCopyIcon />
                        </IconButton>
                      </Box>
                      <Box
                        sx={{
                          width: "100%",
                          flex: 1,
                          minHeight: 0,
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          borderRadius: "8px",
                          padding: "16px",
                          overflowY: "auto",
                          overflowX: "hidden",
                          wordWrap: "break-word",
                          wordBreak: "break-word",
                          position: "relative",
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            my: 2,
                            width: "100%",
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            textAlign: "left",
                            fontFamily: "monospace",
                            fontSize: { xs: 10, sm: 12 },
                            color: theme.palette.text.primary,
                          }}
                        >
                          {textContent}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    // Other reports - show DataGrid table
                    <Box sx={{ height: { xs: 400, sm: 500 }, width: "100%" }}>
                      <DataGrid
                        rows={rows}
                        columns={columnsWithUppercase}
                        pageSize={20}
                        rowsPerPageOptions={[20]}
                        rowHeight={25}
                        sx={{
                          border: "none",
                          "& .MuiDataGrid-cell": {
                            color: theme.palette.text.primary,
                            fontSize: { xs: 10, sm: 12 },
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            "&:focus": {
                              outline: "none",
                            },
                          },
                          "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: `${
                              theme.palette.mode === "dark"
                                ? theme.palette.grey[900]
                                : theme.palette.grey[100]
                            } !important`,
                            color: `${theme.palette.text.primary} !important`,
                            fontSize: { xs: 10, sm: 12 },
                            fontWeight: 600,
                            borderBottom: `2px solid ${theme.palette.divider}`,
                          },
                          "& .MuiDataGrid-columnHeader": {
                            backgroundColor: `${
                              theme.palette.mode === "dark"
                                ? theme.palette.grey[900]
                                : theme.palette.grey[100]
                            } !important`,
                            color: `${theme.palette.text.primary} !important`,
                            fontSize: { xs: 10, sm: 12 },
                            fontWeight: 600,
                            "&:focus": {
                              outline: "none",
                            },
                          },
                          "& .MuiDataGrid-columnHeaderTitle": {
                            color: `${theme.palette.text.primary} !important`,
                            fontWeight: 600,
                          },
                          "& .MuiDataGrid-row": {
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          },
                          "& .MuiDataGrid-virtualScroller": {
                            backgroundColor: "transparent",
                          },
                          "& .MuiDataGrid-footerContainer": {
                            borderTop: `1px solid ${theme.palette.divider}`,
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? theme.palette.grey[900]
                                : theme.palette.grey[100],
                          },
                          "& .MuiDataGrid-footerPagination": {
                            color: theme.palette.text.primary,
                          },
                        }}
                        onCellEditCommit={handleCellEdit}
                      />
                    </Box>
                  )
                ) : (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={{ xs: 300, sm: 400 }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontStyle: "italic",
                        fontSize: { xs: 14, sm: 16 },
                      }}
                    >
                      No data available
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </DialogContent>

      {/* Actions */}
      <Box sx={{ px: { xs: 2, sm: 4 }, pb: 3, pt: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="flex-end"
          spacing={{ xs: 1, sm: 2 }}
        >
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={!textContent || showLoader}
            sx={{
              borderRadius: 1,
              fontWeight: 600,
              fontFamily,
              textTransform: "none",
              px: 3,
              minWidth: { xs: "100%", sm: 140 },
            }}
          >
            Download
          </Button>
          <Button
            variant="contained"
            startIcon={<SubmitIcon />}
            onClick={handleSubmit}
            color="success"
            disabled={!textContent || showLoader}
            sx={{
              borderRadius: 1,
              fontWeight: 600,
              fontFamily,
              textTransform: "none",
              px: 3,
              minWidth: { xs: "100%", sm: 140 },
            }}
          >
            Submit Report
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default ReportDataDialog;
